import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, Grid, Paper, Button, CircularProgress,
  Snackbar, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Switch, FormControlLabel, Select, MenuItem,
  FormControl, InputLabel, IconButton, Tooltip, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Icon,
  Divider,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import { invokeApig } from '../libs/awsLib';
import { useContentManager } from '../hooks/useContentManager';
import { getSectionType, FIELD_TYPES, DETAIL_TEMPLATE_FIELDS } from '../config/contentTypes';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    borderBottom: '1px solid #e0e0e0',
  },
  toolbarTitle: { flexGrow: 1 },
  selectors: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: '1px solid #f0f0f0',
    flexWrap: 'wrap',
    alignItems: 'center',
    backgroundColor: '#fafafa',
  },
  selectorControl: { minWidth: 260 },
  tableWrapper: { padding: theme.spacing(2) },
  tableRow: { '&:hover': { backgroundColor: '#fafafa' } },
  noContent: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: '#9e9e9e',
  },
  chip: { fontSize: 11, height: 22 },
  fieldLabel: { fontWeight: 600, marginBottom: 2 },
  sectionChip: {
    backgroundColor: '#ede7f6',
    color: '#4a148c',
    fontSize: 11,
    height: 22,
  },
  domainText: {
    fontSize: 12,
    color: '#757575',
  },
  addBtn: {
    marginLeft: 'auto',
  },
  tableActions: { whiteSpace: 'nowrap' },
}));

// ── Renderiza un campo del formulario según su tipo ──────────────────────────
function FieldInput({ field, value, onChange }) {
  switch (field.type) {
    case 'richtext':
      return (
        <textarea
          style={{
            width: '100%', fontFamily: 'inherit', fontSize: 14,
            padding: 8, border: '1px solid #c4c4c4', borderRadius: 4,
            resize: 'vertical', minHeight: 80, boxSizing: 'border-box',
          }}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Ingresa ${field.label.toLowerCase()}...`}
        />
      );
    case 'boolean':
      return (
        <FormControlLabel
          control={<Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} color="primary" />}
          label={value ? 'Sí' : 'No'}
        />
      );
    case 'date':
      return (
        <TextField
          type="date" fullWidth size="small"
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      );
    case 'number':
      return (
        <TextField
          type="number" fullWidth size="small"
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          placeholder={`Ingresa ${field.label.toLowerCase()}...`}
        />
      );
    default:
      return (
        <TextField
          fullWidth size="small"
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          placeholder={`Ingresa ${field.label.toLowerCase()}...`}
        />
      );
  }
}

// ── Componente principal ─────────────────────────────────────────────────────
function ContentPage() {
  const classes = useStyles();
  const { loadContent, saveContent, isSaving } = useContentManager();

  const [designs, setDesigns]                 = useState([]);
  const [selectedDesign, setSelectedDesign]   = useState('');
  const [designData, setDesignData]           = useState(null);
  const [sections, setSections]               = useState([]);
  const [selectedSectionKey, setSelectedSectionKey] = useState('');
  const [sectionData, setSectionData]         = useState(null);
  const [pageContent, setPageContent]         = useState({});
  const [items, setItems]                     = useState([]);

  const [loadingDesigns, setLoadingDesigns]   = useState(true);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingContent, setLoadingContent]   = useState(false);

  const [detailPageOptions, setDetailPageOptions]       = useState([]);
  const [selectedDetailPageId, setSelectedDetailPageId] = useState('');
  const [detailItems, setDetailItems]                   = useState([]);
  const [loadingDetail, setLoadingDetail]               = useState(false);

  const [openDialog, setOpenDialog]           = useState(false);
  const [dialogData, setDialogData]           = useState({});
  const [dialogIndex, setDialogIndex]         = useState(null);
  const [dialogError, setDialogError]         = useState('');

  const [openToast, setOpenToast]             = useState(false);
  const [toastMsg, setToastMsg]               = useState('');
  const [toastSeverity, setToastSeverity]     = useState('success');

  // ── Carga inicial: diseños del usuario ───────────────────────────────────

  useEffect(() => {
    loadDesigns();
  }, []);

  const loadDesigns = async () => {
    setLoadingDesigns(true);
    try {
      const res = await invokeApig({ path: '/design/list', method: 'POST', body: {} });
      if (res.Status === 0 || res.statusCode === 200) {
        setDesigns(res.designs || []);
      }
    } catch (e) {
      showToast('Error al cargar los diseños', 'error');
    } finally {
      setLoadingDesigns(false);
    }
  };

  // ── Al seleccionar diseño: cargar secciones de tipo lista ─────────────────

  const handleDesignChange = async (e) => {
    const designId = e.target.value;
    setSelectedDesign(designId);
    setSelectedSectionKey('');
    setSectionData(null);
    setItems([]);
    setPageContent({});
    setDetailItems([]);
    setDetailPageOptions([]);
    setSelectedDetailPageId('');

    const design = designs.find((d) => d.designId === designId);
    setDesignData(design || null);

    if (!designId) return;

    setLoadingSections(true);
    try {
      const res = await invokeApig({
        path:   '/site/design/list',
        method: 'POST',
        body:   { designId },
      });

      const pages = res.designs || res.pages || [];

      // Todas las páginas guiadas son candidatas a página de detalle
      const detailOpts = [];
      pages.forEach((page) => {
        if (page.pageType !== 'guided') return;
        detailOpts.push({ pageId: page.pageId, title: page.title || page.pageId, slug: page.slug || '' });
      });
      setDetailPageOptions(detailOpts);

      // Secciones de lista editables en ContentPage
      const extracted = [];
      pages.forEach((page) => {
        if (page.pageType !== 'guided') return;
        const guidedSections = page.metadata?.guidedSections || [];
        guidedSections.forEach((sec) => {
          const secSType = getSectionType(sec.type);
          // Incluir si fue guardada como lista O si el tipo soporta listas (retrocompatibilidad)
          if (!sec.isList && !secSType?.canBelist) return;

          // Fallback de fields para detail_block creados antes de DETAIL_TEMPLATE_FIELDS
          let fields = sec.fields || [];
          if (secSType?.isDetailTemplate && fields.length === 0) {
            const tmpl = sec.layout?.template || 'article';
            fields = DETAIL_TEMPLATE_FIELDS[tmpl] || DETAIL_TEMPLATE_FIELDS.article;
          }

          extracted.push({
            key:            `${page.pageId}__${sec.id}`,
            pageId:         page.pageId,
            pageTitle:      page.title || page.pageId,
            sectionId:      sec.id,
            label:          sec.label,
            type:           sec.type,
            fields,
            detailPageSlug: sec.detailPageSlug || null,
            detailPageId:   sec.detailPageId   || null,
          });
        });
      });

      setSections(extracted);
    } catch (e) {
      showToast('Error al cargar las secciones del diseño', 'error');
    } finally {
      setLoadingSections(false);
    }
  };

  // ── Carga ítems de la página de detalle (escanea todas las secciones) ────────

  const loadDetailItemsForPage = async (designId, detailPageId) => {
    if (!designId || !detailPageId) return;
    setLoadingDetail(true);
    try {
      const result = await loadContent(designId, detailPageId);
      const content = result.content || {};
      // Recoge todos los ítems con slug de cualquier sección del detalle
      const all = Object.values(content).flat().filter((it) => it && it.slug);
      setDetailItems(all);
    } catch {
      setDetailItems([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  // ── Al seleccionar sección: cargar ítems ──────────────────────────────────

  const handleSectionChange = async (e) => {
    const key = e.target.value;
    setSelectedSectionKey(key);

    const sec = sections.find((s) => s.key === key);
    setSectionData(sec || null);
    setItems([]);
    setPageContent({});
    setDetailItems([]);

    // Preselecciona la página de detalle si la sección ya la tiene configurada
    const autoDetailId = sec?.detailPageId || '';
    setSelectedDetailPageId(autoDetailId);

    if (!sec || !designData?.designId) return;

    setLoadingContent(true);
    try {
      const result = await loadContent(designData.designId, sec.pageId);
      const content = result.content || {};
      setPageContent(content);
      setItems(content[sec.sectionId] || []);
    } catch (e) {
      showToast('Error al cargar el contenido', 'error');
    } finally {
      setLoadingContent(false);
    }

    // Carga los ítems de detalle si hay página configurada
    if (autoDetailId) {
      await loadDetailItemsForPage(designData.designId, autoDetailId);
    }
  };

  // ── Al seleccionar página de detalle manualmente ──────────────────────────

  const handleDetailPageChange = async (e) => {
    const pageId = e.target.value;
    setSelectedDetailPageId(pageId);
    setDetailItems([]);
    if (pageId && designData?.designId) {
      await loadDetailItemsForPage(designData.designId, pageId);
    }
  };

  // ── CRUD de ítems ─────────────────────────────────────────────────────────

  const handleOpenAdd = () => {
    setDialogData({});
    setDialogIndex(null);
    setDialogError('');

    // Auto-seleccionar si solo hay una página de detalle disponible
    if (!selectedDetailPageId && sType?.canBelist) {
      const available = detailPageOptions.filter((p) => p.pageId !== sectionData?.pageId);
      if (available.length === 1) {
        setSelectedDetailPageId(available[0].pageId);
        loadDetailItemsForPage(designData?.designId, available[0].pageId);
      }
    }

    setOpenDialog(true);
  };

  const handleOpenEdit = (index) => {
    const item = items[index];
    setDialogData({ ...item });
    setDialogIndex(index);
    setDialogError('');

    // Detectar página de detalle desde la URL guardada si aún no hay una seleccionada
    if (!selectedDetailPageId && item.url) {
      const parts = item.url.split('/').filter(Boolean);
      if (parts.length >= 2) {
        const found = detailPageOptions.find((p) => p.slug === parts[0]);
        if (found) {
          setSelectedDetailPageId(found.pageId);
          loadDetailItemsForPage(designData?.designId, found.pageId);
        }
      }
    }

    setOpenDialog(true);
  };

  const handleDelete = useCallback(async (index) => {
    const updated = items.filter((_, i) => i !== index);
    await persistItems(updated);
  }, [items, pageContent, sectionData, designData]); // eslint-disable-line

  const handleConfirm = async () => {
    const required = (sectionData?.fields || []).filter((f) => f.required);
    const missing  = required.filter((f) => !dialogData[f.key]);
    if (missing.length > 0) {
      setDialogError(`Campos requeridos: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }
    const finalData = { ...dialogData };
    const activeDetailSlug = selectedDetailPageId
      ? (detailPageOptions.find((p) => p.pageId === selectedDetailPageId)?.slug || '')
      : (sectionData?.detailPageSlug || '');
    if (activeDetailSlug && finalData.slug) {
      finalData.url = `/${activeDetailSlug}/${finalData.slug}`;
    }
    const updated = [...items];
    if (dialogIndex === null) {
      updated.push({ ...finalData });
    } else {
      updated[dialogIndex] = { ...finalData };
    }
    setOpenDialog(false);
    await persistItems(updated);
  };

  const persistItems = async (updatedItems) => {
    if (!designData?.designId || !sectionData) return;
    const newContent = { ...pageContent, [sectionData.sectionId]: updatedItems };
    const result = await saveContent(designData.designId, sectionData.pageId, newContent);
    if (result.success) {
      setPageContent(newContent);
      setItems(updatedItems);
      showToast('Guardado correctamente');
    } else {
      showToast('Error al guardar', 'error');
    }
  };

  // ── Toast ─────────────────────────────────────────────────────────────────

  const showToast = (msg, severity = 'success') => {
    setToastMsg(msg);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  // ── Columnas visibles en la tabla (primeras 3 del tipo de sección) ─────────

  const visibleFields = (sectionData?.fields || []).slice(0, 3);

  const renderCellValue = (field, value) => {
    if (value === undefined || value === null || value === '') return '—';
    if (field.type === 'boolean') {
      return (
        <Chip
          label={value ? 'Sí' : 'No'} size="small" className={classes.chip}
          style={{ backgroundColor: value ? '#e8f5e9' : '#fce4ec', color: value ? '#2e7d32' : '#c62828' }}
        />
      );
    }
    if (field.type === 'image') {
      return value.startsWith('http') || value.startsWith('/')
        ? <img src={value} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} onError={(e) => { e.target.style.display = 'none'; }} />
        : <Typography variant="caption" noWrap style={{ maxWidth: 120, display: 'block' }}>{value}</Typography>;
    }
    return (
      <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
        {String(value).substring(0, 60)}{String(value).length > 60 ? '…' : ''}
      </Typography>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const sType = sectionData ? getSectionType(sectionData.type) : null;

  return (
    <Grid container spacing={2} className={classes.root} justifyContent="center">
      <Grid item xs={12} sm={12} md={11} lg={10}>
        <Paper>

          {/* Toolbar */}
          <div className={classes.toolbar}>
            <div className={classes.toolbarTitle}>
              <Typography variant="h6" style={{ lineHeight: 1.1 }}>Contenido</Typography>
              <Typography variant="caption" style={{ color: '#757575' }}>
                Gestiona el contenido dinámico de tus sitios
              </Typography>
            </div>
          </div>

          {/* Selectores */}
          <div className={classes.selectors}>
            <FormControl className={classes.selectorControl} size="small" variant="outlined">
              <InputLabel>Diseño</InputLabel>
              <Select
                value={selectedDesign}
                onChange={handleDesignChange}
                label="Diseño"
                disabled={loadingDesigns}
              >
                {loadingDesigns ? (
                  <MenuItem disabled><CircularProgress size={16} /></MenuItem>
                ) : designs.length === 0 ? (
                  <MenuItem disabled>Sin diseños disponibles</MenuItem>
                ) : designs.map((d) => (
                  <MenuItem key={d.designId} value={d.designId}>
                    <div>
                      <Typography variant="body2">{d.name}</Typography>
                      {d.domain && <Typography className={classes.domainText}>{d.domain}</Typography>}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedDesign && (
              <FormControl className={classes.selectorControl} size="small" variant="outlined">
                <InputLabel>Sección de contenido</InputLabel>
                <Select
                  value={selectedSectionKey}
                  onChange={handleSectionChange}
                  label="Sección de contenido"
                  disabled={loadingSections}
                >
                  {loadingSections ? (
                    <MenuItem disabled><CircularProgress size={16} /></MenuItem>
                  ) : sections.length === 0 ? (
                    <MenuItem disabled>Sin secciones de lista en el diseño</MenuItem>
                  ) : sections.map((sec) => (
                    <MenuItem key={sec.key} value={sec.key}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {sType?.icon && <Icon style={{ fontSize: 18, color: '#7b1fa2' }}>{getSectionType(sec.type)?.icon}</Icon>}
                        <div>
                          <Typography variant="body2">{sec.label}</Typography>
                          <Typography className={classes.domainText}>{sec.pageTitle}</Typography>
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedSectionKey && sType?.canBelist && detailPageOptions.length > 0 && (
              <FormControl className={classes.selectorControl} size="small" variant="outlined">
                <InputLabel>Página de detalle</InputLabel>
                <Select
                  value={selectedDetailPageId}
                  onChange={handleDetailPageChange}
                  label="Página de detalle"
                  disabled={loadingDetail}
                >
                  <MenuItem value=""><em>Sin página de detalle</em></MenuItem>
                  {detailPageOptions.map((p) => (
                    <MenuItem key={p.pageId} value={p.pageId}>{p.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedSectionKey && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Icons.Add />}
                className={classes.addBtn}
                onClick={handleOpenAdd}
                disabled={isSaving}
              >
                Agregar
              </Button>
            )}
          </div>

          {/* Cuerpo */}
          <div className={classes.tableWrapper}>
            {!selectedDesign ? (
              <div className={classes.noContent}>
                <Icons.Web style={{ fontSize: 56, opacity: 0.2 }} />
                <Typography variant="body1" style={{ marginTop: 8 }}>
                  Selecciona un diseño para comenzar
                </Typography>
              </div>
            ) : !selectedSectionKey ? (
              <div className={classes.noContent}>
                <Icons.ViewList style={{ fontSize: 56, opacity: 0.2 }} />
                <Typography variant="body1" style={{ marginTop: 8 }}>
                  {sections.length === 0 && !loadingSections
                    ? 'El diseño no tiene secciones de lista. Configúralas en el Diseñador Guiado.'
                    : 'Selecciona una sección de contenido'}
                </Typography>
              </div>
            ) : loadingContent ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <CircularProgress />
              </div>
            ) : (
              <>
                {/* Encabezado de sección */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  {sType && (
                    <Icon style={{ color: '#7b1fa2', fontSize: 20 }}>{sType.icon}</Icon>
                  )}
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {sectionData?.label}
                  </Typography>
                  <Chip
                    label={`${items.length} elemento${items.length !== 1 ? 's' : ''}`}
                    size="small" className={classes.sectionChip}
                  />
                </div>

                <Divider style={{ marginBottom: 16 }} />

                {items.length === 0 ? (
                  <div className={classes.noContent}>
                    <Icons.Inbox style={{ fontSize: 48, opacity: 0.25 }} />
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                      Sin elementos. Agrega el primero.
                    </Typography>
                    <Button
                      variant="outlined" color="primary" startIcon={<Icons.Add />}
                      onClick={handleOpenAdd} style={{ marginTop: 16 }}
                    >
                      Agregar elemento
                    </Button>
                  </div>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: 40 }}>#</TableCell>
                        {visibleFields.map((f) => (
                          <TableCell key={f.key}>{f.label}</TableCell>
                        ))}
                        <TableCell align="center" className={classes.tableActions}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={index} className={classes.tableRow}>
                          <TableCell>{index + 1}</TableCell>
                          {visibleFields.map((f) => (
                            <TableCell key={f.key}>{renderCellValue(f, item[f.key])}</TableCell>
                          ))}
                          <TableCell align="center" className={classes.tableActions}>
                            <Tooltip title="Ver detalle" enterDelay={400}>
                              <IconButton
                                size="small"
                                style={{ color: '#7b1fa2' }}
                                onClick={() => window.open(
                                  `/content-preview/${designData.designId}/${sectionData.pageId}/${sectionData.sectionId}/${index}`,
                                  '_blank'
                                )}
                              >
                                <Icons.OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar" enterDelay={400}>
                              <IconButton size="small" onClick={() => handleOpenEdit(index)} style={{ color: '#1976d2' }}>
                                <Icons.Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar" enterDelay={400}>
                              <IconButton
                                size="small" onClick={() => handleDelete(index)}
                                style={{ color: '#f44336' }} disabled={isSaving}
                              >
                                {isSaving ? <CircularProgress size={14} /> : <Icons.Delete fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </div>
        </Paper>
      </Grid>

      {/* ── Dialog agregar / editar ────────────────────────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth maxWidth="sm"
      >
        <DialogTitle>
          {dialogIndex === null ? 'Agregar elemento' : 'Editar elemento'}
          {sectionData && (
            <Typography variant="caption" display="block" style={{ color: '#9e9e9e' }}>
              {sectionData.label}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ paddingTop: 8 }}>

            {/* ── Selector de página de detalle dentro del diálogo ── */}
            {sType?.canBelist && (() => {
              const available = detailPageOptions.filter((p) => p.pageId !== sectionData?.pageId);
              if (available.length === 0) return null;
              return (
                <Grid item xs={12}>
                  <Typography variant="caption" className={classes.fieldLabel} display="block">
                    Página de detalle
                    <span style={{ color: '#9e9e9e', marginLeft: 4, fontWeight: 400 }}>(vincula el slug al elemento)</span>
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedDetailPageId}
                      onChange={handleDetailPageChange}
                      displayEmpty
                      disabled={loadingDetail}
                    >
                      <MenuItem value=""><em>Sin página de detalle</em></MenuItem>
                      {available.map((p) => (
                        <MenuItem key={p.pageId} value={p.pageId}>{p.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              );
            })()}

            {/* ── Slug selector + URL (siempre visibles cuando hay página de detalle) ── */}
            {selectedDetailPageId && (() => {
              const detailPageSlug = detailPageOptions.find((p) => p.pageId === selectedDetailPageId)?.slug || '';
              const slugOptions = detailItems.filter((it) => it.slug);
              return (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" className={classes.fieldLabel} display="block">
                      Elemento de detalle
                      <span style={{ color: '#f44336', marginLeft: 2 }}>*</span>
                    </Typography>
                    {loadingDetail ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" style={{ color: '#757575' }}>Cargando elementos...</Typography>
                      </div>
                    ) : slugOptions.length > 0 ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={dialogData.slug || ''}
                          onChange={(e) => {
                            const chosen = slugOptions.find((it) => it.slug === e.target.value);
                            setDialogData((prev) => ({
                              ...prev,
                              slug: e.target.value,
                              ...(chosen?.titulo  && !prev.titulo  ? { titulo:  chosen.titulo  } : {}),
                              ...(chosen?.nombre  && !prev.nombre  ? { nombre:  chosen.nombre  } : {}),
                              ...(chosen?.imagen  && !prev.imagen  ? { imagen:  chosen.imagen  } : {}),
                            }));
                          }}
                          displayEmpty
                        >
                          <MenuItem value=""><em>Seleccionar elemento...</em></MenuItem>
                          {slugOptions.map((it) => (
                            <MenuItem key={it.slug} value={it.slug}>
                              {it.titulo || it.nombre || it.slug}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <div>
                        <TextField fullWidth size="small" value={dialogData.slug || ''}
                          onChange={(e) => setDialogData((prev) => ({ ...prev, slug: e.target.value }))}
                          placeholder="ej: mi-articulo"
                        />
                        <Typography variant="caption" style={{ color: '#e65100', display: 'block', marginTop: 4 }}>
                          La página de detalle no tiene elementos. Agrégalos primero.
                        </Typography>
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" className={classes.fieldLabel} display="block">
                      Enlace
                      <span style={{ color: '#9e9e9e', marginLeft: 4, fontWeight: 400 }}>(calculado automáticamente)</span>
                    </Typography>
                    <div style={{ padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#f5f5f5', fontSize: 13, color: '#555', fontFamily: 'monospace', minHeight: 34 }}>
                      {dialogData.slug && detailPageSlug
                        ? `/${detailPageSlug}/${dialogData.slug}`
                        : <span style={{ color: '#bdbdbd' }}>Se completa al seleccionar el elemento</span>
                      }
                    </div>
                  </Grid>
                </>
              );
            })()}

            {(sectionData?.fields || []).map((field) => {
              // Si hay página de detalle activa, omitir slug y url del loop (ya se muestran arriba)
              if (selectedDetailPageId && (field.key === 'slug' || field.key === 'url')) return null;

              // URL auto-calculada cuando la sección tenía detailPageSlug configurado (sin selector activo)
              if (field.key === 'url' && sectionData?.detailPageSlug) {
                const detailPageSlug = sectionData.detailPageSlug;
                return (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <Typography variant="caption" className={classes.fieldLabel} display="block">
                      Enlace
                      <span style={{ color: '#9e9e9e', marginLeft: 4, fontWeight: 400 }}>(calculado automáticamente)</span>
                    </Typography>
                    <div style={{ padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#f5f5f5', fontSize: 13, color: '#555', fontFamily: 'monospace', minHeight: 34 }}>
                      {dialogData.slug && detailPageSlug
                        ? `/${detailPageSlug}/${dialogData.slug}`
                        : <span style={{ color: '#bdbdbd' }}>Se completa al ingresar el slug</span>
                      }
                    </div>
                  </Grid>
                );
              }
              return (
                <Grid item xs={12} sm={field.type === 'richtext' ? 12 : 6} key={field.key}>
                  <Typography variant="caption" className={classes.fieldLabel} display="block">
                    {field.label}
                    {field.required && <span style={{ color: '#f44336', marginLeft: 2 }}>*</span>}
                    <span style={{ color: '#9e9e9e', marginLeft: 4, fontWeight: 400 }}>
                      ({FIELD_TYPES.find((ft) => ft.value === field.type)?.label || field.type})
                    </span>
                  </Typography>
                  <FieldInput
                    field={field}
                    value={dialogData[field.key]}
                    onChange={(val) => setDialogData((prev) => ({ ...prev, [field.key]: val }))}
                  />
                </Grid>
              );
            })}
          </Grid>
          {dialogError && (
            <Alert severity="error" style={{ marginTop: 12 }}>{dialogError}</Alert>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '10px 24px' }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleConfirm} disabled={isSaving}>
            {isSaving ? <CircularProgress size={20} color="inherit" /> : (dialogIndex === null ? 'Agregar' : 'Guardar cambios')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Snackbar open={openToast} autoHideDuration={5000} onClose={() => setOpenToast(false)}>
        <Alert onClose={() => setOpenToast(false)} severity={toastSeverity}>
          {toastMsg}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default ContentPage;
